import { useState, useEffect } from "react";
import css from "./lesson.module.css";
import LessonInfo from "./lessonInfo";
import WrapVideo from "./wrapVideo";
import WordsComponent from "./wordsComponent";
import Discuss from "./discuss";
import QuestionCardExample from "./newTestings/QuestionCardExample";
import Dialogues from "./PracticeDialogues/Dialogues";
import CompletionScreen from "./CompletionScreen";
import { toggleVideoFavorite, getFavoriteStatus } from '../../lib/favoriteApi';
import { getLanguageLevelById } from "../../lib/getLanguageLevels";
import { getVideoCategories } from "../../lib/videoCategoryApi";
import { useTranslation } from "react-i18next";
import like from '../../img/like.png';
import likeOff from '../../img/likeOff.png';
import { useNavigate, useParams } from "react-router-dom";

const LessonBlock = ({ lesson, authUser }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [videoP, setVideoP] = useState(true);
  const [learnWords, setLearnWords] = useState(false);
  const [videoQ, setVideoQ] = useState(false);
  const [dialogues, setDialogues] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [selectedButton, setSelectedButton] = useState("speak");
  const [showRegisterBanner, setShowRegisterBanner] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [favoriteId, setFavoriteId] = useState(null);
  const [languageLevel, setLanguageLevel] = useState("");
  const [levels, setLevels] = useState([]);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState(authUser?.videoLessonsAll || []);
  const navigate = useNavigate();
  const [levelStyles, setLevelStyles] = useState({
    bgColor: "#e0e0e0",
    textColor: "#000",
    fullName: "Unknown Level",
    order: "-",
  });

  const levelMapping = {
    "pre-A1": {
      fullName: "Pre-Beginner",
      order: 0,
      bgColor: "#c8e6c9",
      textColor: "#1b5e20",
      name: "pre-A1",
    },
    A1: {
      fullName: "Beginner",
      order: 1,
      bgColor: "#e8f5e9",
      textColor: "#2e7d32",
      name: "A1",
    },
    A2: {
      fullName: "Elementary",
      order: 2,
      bgColor: "#fff3e0",
      textColor: "#f57c00",
      name: "A2",
    },
    B1: {
      fullName: "Intermediate",
      order: 3,
      bgColor: "#e3f2fd",
      textColor: "#1565c0",
      name: "B1",
    },
    B2: {
      fullName: "Upper-Intermediate",
      order: 4,
      bgColor: "#ede7f6",
      textColor: "#673ab7",
      name: "B2",
    },
    C1: {
      fullName: "Advanced",
      order: 5,
      bgColor: "#ffebee",
      textColor: "#d32f2f",
      name: "C1",
    },
    C2: {
      fullName: "Proficiency",
      order: 6,
      bgColor: "#fce4ec",
      textColor: "#c2185b",
      name: "C2",
    },
  };

  //console.log(authUser)

  useEffect(() => {
    if (authUser?.isAuthenticated) {
      setShowRegisterBanner(false);
    } else {
      setShowRegisterBanner(true);
    }
  }, [])

  // Check lesson completion status dynamically
  useEffect(() => {
    if (authUser?.videoLessonsAll) {
      setVideos(authUser.videoLessonsAll);
    }
  }, [authUser?.videoLessonsAll]);

  // Function to check and update completion status
  const checkAndUpdateCompletion = async () => {
    if (!authUser?.isAuthenticated || !lesson?.id) return;
    
    try {
      console.log('🔄 Checking lesson completion status...');
      console.log('Current lesson ID:', lesson.id);
      console.log('Current lesson.video.id:', lesson.video?.id);
      
      // Use the global refreshAuthUser function if available, otherwise fetch directly
      let updatedVideos = [];
      
      if (window.refreshAuthUser) {
        console.log('Using global refreshAuthUser...');
        const freshAuthUser = await window.refreshAuthUser();
        updatedVideos = freshAuthUser?.videoLessonsAll || [];
      } else {
        console.log('Fetching directly from API...');
        // Fallback: fetch directly
        const response = await fetch('/api/v1/user-profile/current', {
          credentials: 'include'
        });
        
        if (response.ok) {
          const userData = await response.json();
          updatedVideos = userData.videoLessonsAll || [];
        }
      }
      
      console.log('📦 Fetched videos from backend:', updatedVideos.length, 'videos');
      
      if (updatedVideos.length === 0) {
        console.warn('⚠️ No videos returned from backend');
        return;
      }
      
      setVideos(updatedVideos);
      
      // Try to find the current video by matching lesson.id with video.id in the array
      // The videoLessonsAll array contains objects with structure: { id: X, video: { id: Y }, ... }
      // We need to match lesson.id (from URL) with the video.id field
      let currentVideo = updatedVideos.find((v) => v.video?.id === lesson.id);
      
      // Fallback: if not found, try matching with the top-level id
      if (!currentVideo) {
        currentVideo = updatedVideos.find((v) => v.id === lesson.id);
      }
      
      console.log('Found currentVideo:', currentVideo);
      
      if (!currentVideo) {
        console.warn('⚠️ No video data found for lesson:', lesson.id);
        console.warn('Available video.id values:', updatedVideos.map(v => v.video?.id));
        console.warn('Available top-level id values:', updatedVideos.map(v => v.id));
        return;
      }
      
      const isCompleted = currentVideo?.is_completed || false;
      
      console.log('✅ Completion check result:', {
        lessonId: lesson.id,
        isCompleted,
        videoLessonId: currentVideo.id,
        videoId: currentVideo.video?.id
      });
      
      if (isCompleted && !showCompletion) {
        // Show completion screen if lesson just got completed
        console.log('🎉 Lesson completed! Showing completion screen...');
        setShowCompletion(true);
        setVideoP(false);
        setLearnWords(false);
        setVideoQ(false);
        setDialogues(false);
      }
    } catch (error) {
      console.error('❌ Error checking completion status:', error);
    }
  };

  // Initial completion check on mount
  useEffect(() => {
    const checkCompletion = () => {
      // Try to find the current video by matching lesson.id with video.id in the array
      let currentVideo = videos.find((v) => v.video?.id === lesson?.id);
      
      // Fallback: if not found, try matching with the top-level id
      if (!currentVideo) {
        currentVideo = videos.find((v) => v.id === lesson?.id);
      }
      
      const isCompleted = currentVideo?.is_completed || false;
      
      console.log('Initial completion check:', {
        lessonId: lesson?.id,
        foundVideo: !!currentVideo,
        isCompleted
      });
      
      if (isCompleted) {
        // On initial load or when lesson is completed, show completion screen
        setShowCompletion(true);
        setVideoP(false);
        setLearnWords(false);
        setVideoQ(false);
        setDialogues(false);
      }
    };

    if (lesson && videos.length > 0) {
      checkCompletion();
    }
  }, [lesson, videos]);

  // Fetch lesson data and categories
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const categoriesData = await getVideoCategories();
        setCategories(categoriesData.results);

        // Fetch language level and set styles
        if (lesson?.video?.languageLevel) {
          const level = await getLanguageLevelById(lesson.video.languageLevel);
          setLanguageLevel(level.name);
          if (levelMapping[level.name]) {
            setLevelStyles(levelMapping[level.name]);
          }
        }

        // Fetch favorite status
        if (lesson?.video?.id) {
          const { isFavorited, favoriteId: favoriteIdValue } = await getFavoriteStatus(lesson.video.id);
          setIsLiked(isFavorited);
          setFavoriteId(favoriteIdValue);
        }
      } catch (error) {
        console.error("Error fetching lesson data:", error);
        // Fallback to lesson data if API call fails
        if (lesson) {
          const isFavorited = lesson.is_favorited || false;
          const favoriteIdValue = lesson.favorite_id || null;
          setIsLiked(isFavorited);
          setFavoriteId(favoriteIdValue);
        }
      }
    };

    if (lesson) {
      fetchData();
    }
  }, [lesson]);

  const handleSelect = (button) => {
    setSelectedButton(button);
  };

  // Expose handlers globally for Dialogues component to switch views
  useEffect(() => {
    window.lessonInfoHandlers = {
      handleSelect: (button) => {
        let targetIndex = -1;
        
        if (button === 'words') {
          setShowCompletion(false);
          setVideoP(false);
          setLearnWords(true);
          setDialogues(false);
          setVideoQ(false);
          setSelectedButton("learn");
          targetIndex = 1; // Second button (Study Words)
        } else if (button === 'speak') {
          setShowCompletion(false);
          setVideoP(true);
          setLearnWords(false);
          setVideoQ(false);
          setDialogues(false);
          setSelectedButton("speak");
          targetIndex = 0; // First button (Watch Video)
        } else if (button === 'test') {
          setShowCompletion(false);
          setVideoP(false);
          setLearnWords(false);
          setVideoQ(true);
          setDialogues(false);
          setSelectedButton("discuss");
          targetIndex = 2; // Third button (Check Understanding)
        } else if (button === 'dialogue') {
          setShowCompletion(false);
          setVideoP(false);
          setLearnWords(false);
          setVideoQ(false);
          setDialogues(true);
          setSelectedButton("none");
          targetIndex = 3; // Fourth button (Practice Dialogues)
        }
        
        // Update active state for buttons using data attribute
        if (targetIndex >= 0) {
          setTimeout(() => {
            const choiceWraps = document.querySelectorAll('[data-choice-index]');
            choiceWraps.forEach((el) => {
              const index = parseInt(el.getAttribute('data-choice-index'));
              if (index === targetIndex) {
                el.classList.add('active');
              } else {
                el.classList.remove('active');
              }
            });
          }, 100);
        }
      }
    };

    return () => {
      delete window.lessonInfoHandlers;
    };
  }, []);



  const closeBanner = () => {
    setShowRegisterBanner(false);
  };

  const handleRegister = () => {
    // Navigate to registration page
    window.location.href = "/signup";
  };

  const findParentCategory = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return null;

    if (category.parent === null) {
      return category;
    }

    return categories.find(cat => cat.id === category.parent);
  };

  const getCategoryIcon = (categoryId) => {
    const parentCategory = findParentCategory(categoryId);
    if (!parentCategory) return '🌐';

    const name = parentCategory.name?.toLowerCase() || '';

    // Check for more specific categories first (order matters!)
    if (name.includes('talk') || name.includes('dialogue') || name.includes('діалог') || name.includes('розмов') || name.includes('спілкування') || name.includes('rozmawiać') || name.includes('dialog') || name.includes('socializ')) return '🗣️';
    if (name.includes('all') || name.includes('всі') || name.includes('wszystkie')) return '🌐';
    if (name.includes('travel') || name.includes('подорож') || name.includes('podróż')) return '✈️';
    if (name.includes('work') || name.includes('робот') || name.includes('praca') || name.includes('business') || name.includes('бізнес')) return '💼';
    if (name.includes('education') || name.includes('освіт') || name.includes('edukacja') || name.includes('learn')) return '📚';
    if (name.includes('health') || name.includes('здоров') || name.includes('zdrowie')) return '❤️';
    if (name.includes('news') || name.includes('новин') || name.includes('wiadomości') || name.includes('tech') || name.includes('технолог')) return '📱';
    if (name.includes('leisure') || name.includes('дозвілля') || name.includes('rozrywka') || name.includes('entertainment') || name.includes('розваг')) return '🎮';
    if (name.includes('daily') || name.includes('щоден') || name.includes('codzienn')) return '📅';

    return '🌐';
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : "Unknown Category";
  };

  const handleFavoriteToggle = async () => {
    if (isTogglingFavorite || !lesson?.video?.id) return; // Prevent multiple clicks and ensure video id exists
    setIsTogglingFavorite(true);

    // Store previous state for rollback
    const previousIsLiked = isLiked;
    const previousFavoriteId = favoriteId;

    // Optimistic update
    setIsLiked(!isLiked);

    try {
      const response = await toggleVideoFavorite(lesson.video.id, favoriteId, isLiked);

      if (!isLiked) {
        // Adding to favorites - response should contain the new favorite ID
        setFavoriteId(response.id);
        console.log('Added to favorites with ID:', response.id);
      } else {
        // Removing from favorites
        setFavoriteId(null);
        console.log('Removed from favorites');
      }

      // Fetch updated favorite status to ensure consistency
      const { isFavorited, favoriteId: newFavoriteId } = await getFavoriteStatus(lesson.video.id);
      setIsLiked(isFavorited);
      setFavoriteId(newFavoriteId);
      console.log('Favorite status updated successfully');
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Revert optimistic update on error
      setIsLiked(previousIsLiked);
      setFavoriteId(previousFavoriteId);
      // Show user-friendly error message
      alert(t('Failed to update favorite status. Please try again.'));
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  return (
    <div className={css.wrapAllM}>
      {/* Registration Banner */}
      {showRegisterBanner === true && (
        <div className={css.registerBanner}>
          <div className={css.registerContent}>
            <span className={css.registerIcon}>💾</span>
            <span className={css.registerText}>{t("Register to save your progress")}</span>
            <button className={css.registerButton} onClick={handleRegister}>
              {t("Register")}
            </button>
          </div>
          <button className={css.closeBanner} onClick={closeBanner}>×</button>
        </div>
      )}

      {/* Learning Path Breadcrumb */}
      <div className={showRegisterBanner ? (`${css.learningPath} ${css.double}`) : (`${css.learningPath}`)}>
        <div className={css.pathItem} onClick={() => navigate('/')}>
          <div className={css.pathIcon}>🏠</div>
          <span>{t("Home")}</span>
        </div>
        <span className={css.pathSeparator}>→</span>
        <div className={css.pathItem} onClick={() => {
          // Store the selected category in localStorage before navigating
          if (lesson?.video?.category) {
            const categoryId = lesson.video.category;

            // Find parent category if this is a child category
            const category = categories.find(cat => cat.id === categoryId);
            let targetCategoryId = categoryId;

            if (category && category.parent !== null) {
              // If it's a child category, use the parent category
              targetCategoryId = category.parent;
            }

            // Store the category selection in localStorage
            const purposeData = {
              categories: [targetCategoryId]
            };
            localStorage.setItem("selectedPurpose", JSON.stringify(purposeData));

            // Clear any level selection to show all levels for this category
            localStorage.removeItem("selectedLevel");
          }

          navigate('/catalog');
        }}>
          <div className={css.pathIcon}>
            {getCategoryIcon(lesson?.video?.category)}
          </div>
          <span>{getCategoryName(lesson?.video?.category) || t("Category")}</span>
        </div>
        <span className={css.pathSeparator}>→</span>
        <div className={css.currentPath}>{lesson?.video?.name || "Lesson"}</div>
      </div>

      {/* Main Container */}
      <div className={css.mainContainer}>
        {/* Lesson Content */}
        <div className={css.lessonContent}>
          {/* Video/Content Area */}
          <div className={css.leftWrap}>
            {showCompletion && (
              <CompletionScreen 
                lesson={lesson} 
                onRepeat={() => {
                  setShowCompletion(false);
                  setVideoP(true);
                  setLearnWords(false);
                  setVideoQ(false);
                  setDialogues(false);
                  setSelectedButton("speak");
                  
                  // Update active state for Watch Video button
                  setTimeout(() => {
                    const choiceWraps = document.querySelectorAll('[data-choice-index]');
                    choiceWraps.forEach((el, i) => {
                      if (i === 0) {
                        el.classList.add('active');
                      } else {
                        el.classList.remove('active');
                      }
                    });
                  }, 100);
                }}
              />
            )}
            {!showCompletion && videoP && <WrapVideo lesson={lesson} authUser={authUser} videos={videos} setVideos={setVideos} onActivityComplete={checkAndUpdateCompletion} />}
            {!showCompletion && learnWords && (
              <div className={css.wrWhereW}>
                <WordsComponent lesson={lesson} onActivityComplete={checkAndUpdateCompletion} />
              </div>
            )}
            {!showCompletion && videoQ && <QuestionCardExample onActivityComplete={checkAndUpdateCompletion} />}
            {!showCompletion && dialogues && <Dialogues lesson={lesson} authUser={authUser} onActivityComplete={checkAndUpdateCompletion} />}
          </div>
        </div>

        {/* Sidebar */}
        <div className={css.rightWrap}>
          <LessonInfo
            handleSelect={handleSelect}
            lesson={lesson}
            setLearnWords={setLearnWords}
            setVideoP={setVideoP}
            setSelectedButton={setSelectedButton}
            selectedButton={selectedButton}
            setVideoQ={setVideoQ}
            videoQ={videoQ}
            dialogues={dialogues}
            setDialogues={setDialogues}
            isCompletionShown={showCompletion}
          />
        </div>
      </div>
    </div>
  );
};
export default LessonBlock;
